import React from 'react';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import { useCommunity } from '@/contexts/CommunityContext';
import { useTranslation } from 'react-i18next';

interface DiscountBadgeProps {
  amount: number;
  isPercentage: boolean;
  appliesToAll: boolean;
  type?: 'Paid' | 'Free';
  className?: string;
  size?: 'sm' | 'md' | 'lg';
  hideLabel?: boolean;
}

const DiscountBadge = ({ 
  amount, 
  isPercentage, 
  appliesToAll, 
  type = 'Paid',
  className,
  size = 'md',
  hideLabel = false
}: DiscountBadgeProps) => {
  const { getParameter } = useCommunity();
  const currency = getParameter<string>('currency') || '$';
  const partialDiscountAllowed = getParameter<boolean>('partial_discount_allowed') || false;
  const { t } = useTranslation();
  
  // Badge color based on type and whether it applies to all services
  const badgeColor = type === 'Free' 
    ? 'bg-green-600 hover:bg-green-700 text-white'
    : (appliesToAll
        ? 'bg-orange-600 hover:bg-orange-700 text-white'
        : 'bg-orange-500/80 hover:bg-orange-600/80 text-white');
  
  // Size classes
  const sizeClasses = {
    sm: 'text-xs py-0 px-1.5',
    md: 'text-xs py-0.5 px-2',
    lg: 'text-sm py-1 px-2.5'
  };
  
  // Service coverage label
  const coverageLabel = appliesToAll 
    ? t('discount.badge.all_services') 
    : t('discount.badge.selected_services');
  
  return (
    <div className="flex flex-col items-start">
      <Badge 
        className={cn(
          badgeColor,
          'flex items-center font-medium',
          sizeClasses[size],
          className
        )}
      >
        <span className="font-bold">
          {type === 'Free' 
            ? t('discount.badge.free')
            : (isPercentage 
                ? t('discount.badge.percentage_off', { amount })
                : t('discount.badge.amount_save', { amount, currency }))}
        </span>
      </Badge>
      {!hideLabel && partialDiscountAllowed && type !== 'Free' && (
        <span className={cn(
          "text-[10px] font-medium mt-1 px-1",
          appliesToAll ? "text-orange-700" : "text-orange-600/80"
        )}>
          {coverageLabel}
        </span>
      )}
    </div>
  );
};

export default DiscountBadge;
